export const ASSET_CLASSES = {
    IMMEDIATE: 'immediate',      // 少額減価償却資産（10万未満）→即時経費
    LUMP_SUM: 'lump-sum',        // 一括償却資産（10-20万）→3年均等
    SMALL_BIZ: 'small-business', // 青色申告特例（30万未満）→即時経費
    STANDARD: 'standard'         // 通常資産（30万以上）→耐用年数で償却
};

export const ASSET_CLASS_LABELS = {
    [ASSET_CLASSES.IMMEDIATE]: '少額減価償却資産',
    [ASSET_CLASSES.LUMP_SUM]: '一括償却資産',
    [ASSET_CLASSES.SMALL_BIZ]: '少額減価償却資産特例',
    [ASSET_CLASSES.STANDARD]: '通常資産'
};

// 耐用年数テーブル（カテゴリ別）
export const USEFUL_LIFE_TABLE = {
    'パソコン': 4,
    'サーバー': 5,
    '工具器具備品': 4,
    'ソフトウェア': 5,
    '車両': 6,
    '什器': 8,
    '建物（木造）': 22,
    '建物（RC）': 47,
    'その他': 4
};

// 資産カテゴリ一覧
export const ASSET_CATEGORIES = Object.keys(USEFUL_LIFE_TABLE);

// 固定資産科目（仕訳から自動登録の対象）
export const FIXED_ASSET_ACCOUNT_NAMES = ['工具器具備品', '車両運搬具', '建物', '建物附属設備', '構築物', '機械装置', '土地', 'ソフトウェア'];

// 科目名からカテゴリを推定
export const guessCategory = (accountName, description) => {
    const text = `${accountName} ${description}`.toLowerCase();
    if (/パソコン|pc|mac|ノート|デスクトップ/.test(text)) return 'パソコン';
    if (/サーバ|server/.test(text)) return 'サーバー';
    if (/車|トラック|バイク/.test(text)) return '車両';
    if (/ソフト|software|ライセンス/.test(text)) return 'ソフトウェア';
    if (/什器|家具|机|椅子|棚/.test(text)) return '什器';
    if (/建物|木造|rc|鉄/.test(text)) return accountName.includes('木造') ? '建物（木造）' : '建物（RC）';
    return '工具器具備品';
};

// 取得価額から資産区分を自動判定
// isSmallBizEligible: 個人の青色申告者 + 中小法人（資本金1億円以下）
export const classifyAsset = (acquisitionCost, isKojin = false, isCorporation = false) => {
    if (acquisitionCost < 100000) {
        return ASSET_CLASSES.IMMEDIATE;
    }
    if (acquisitionCost < 200000) {
        return ASSET_CLASSES.LUMP_SUM;
    }
    const isSmallBizEligible = isKojin || isCorporation;
    if (isSmallBizEligible && acquisitionCost < 300000) {
        return ASSET_CLASSES.SMALL_BIZ;
    }
    return ASSET_CLASSES.STANDARD;
};

// 定額法による年間償却額を計算
export const calculateDepreciation = (asset, fiscalYear, fiscalEndMonth = 3) => {
    const cost = asset.acquisitionCost;
    const life = asset.usefulLife;
    const purchaseDate = new Date(asset.purchaseDate);
    const businessRatio = (asset.businessRatio || 100) / 100;

    // 資産区分に応じた計算
    if (asset.assetClass === ASSET_CLASSES.IMMEDIATE) {
        // 少額：取得年度に全額経費
        const purchaseYear = purchaseDate.getFullYear();
        if (purchaseYear === fiscalYear) {
            return Math.floor(cost * businessRatio);
        }
        return 0;
    }

    if (asset.assetClass === ASSET_CLASSES.LUMP_SUM) {
        // 一括償却：3年均等（月割りなし）
        const purchaseYear = purchaseDate.getFullYear();
        const yearsElapsed = fiscalYear - purchaseYear;
        if (yearsElapsed >= 0 && yearsElapsed < 3) {
            return Math.floor((cost / 3) * businessRatio);
        }
        return 0;
    }

    if (asset.assetClass === ASSET_CLASSES.SMALL_BIZ) {
        // 青色申告特例：取得年度に全額経費
        const purchaseYear = purchaseDate.getFullYear();
        if (purchaseYear === fiscalYear) {
            return Math.floor(cost * businessRatio);
        }
        return 0;
    }

    // 通常資産：定額法（月割り計算）
    const annualAmount = Math.floor(cost / life);
    const purchaseMonth = purchaseDate.getMonth() + 1;
    const purchaseYear = purchaseDate.getFullYear();
    const maxDepreciable = cost - 1; // 備忘価額1円

    // ヘルパー: 指定年度の月割計算（endMonthは年度の決算月）
    const calcMonths = (year) => {
        if (year !== purchaseYear) return 12; // 2年目以降は12ヶ月満額
        if (fiscalEndMonth === 12) {
            return 12 - purchaseMonth + 1;
        }
        if (purchaseMonth <= fiscalEndMonth) {
            return fiscalEndMonth - purchaseMonth + 1;
        }
        return 12 - purchaseMonth + fiscalEndMonth + 1;
    };

    // 過去年度の償却累計を計算
    let alreadyDepreciated = 0;
    for (let y = purchaseYear; y < fiscalYear; y++) {
        const months = calcMonths(y);
        const yearAmount = Math.floor((annualAmount * months / 12) * businessRatio);
        alreadyDepreciated += yearAmount;
        if (alreadyDepreciated >= maxDepreciable) {
            alreadyDepreciated = maxDepreciable;
            break;
        }
    }

    const remaining = maxDepreciable - alreadyDepreciated;
    if (remaining <= 0) return 0;

    // 当期の基本額
    const isFirstYear = purchaseYear === fiscalYear;
    const thisYearMonths = calcMonths(fiscalYear);
    const thisYearBase = Math.floor((annualAmount * thisYearMonths / 12) * businessRatio);

    // 最終年度は残額全部（Math.minで自動適用）
    return Math.min(thisYearBase, remaining);
};
